import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Capacitor } from '@capacitor/core';
import installArtRelay from './utils/artRelay';
import { notifyAppReady } from './services/appUpdate';
import { StatusBar, Style } from '@capacitor/status-bar';
import dropFocusAfterTap from './utils/dropFocusAfterTap';

// Card images that fail to load are retried through our own relay. Installed
// before the first render so the very first image is covered.
installArtRelay();
// A web bundle that came over the air is rolled back unless it reports in.
notifyAppReady();

// Setup safe areas based on platform
const setupSafeAreas = async () => {
  const platform = Capacitor.getPlatform();
  const root = document.documentElement;

  if (platform === 'ios') {
    // iOS: Use native env() values
    root.style.setProperty('--safe-area-top', 'env(safe-area-inset-top, 0px)');
    root.style.setProperty('--safe-area-bottom', 'env(safe-area-inset-bottom, 0px)');
    root.style.setProperty('--safe-area-left', 'env(safe-area-inset-left, 0px)');
    root.style.setProperty('--safe-area-right', 'env(safe-area-inset-right, 0px)');
  } else if (platform === 'android') {
    try {
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#00000000' });
      await StatusBar.setOverlaysWebView({ overlay: true });
    } catch (e) {
      console.log('StatusBar setup error:', e);
    }
    // These were fixed at 28px and 45px, with a note saying MainActivity would
    // replace them with real numbers. MainActivity never did - it only
    // registers the camera plugin - so every Android phone has been running on
    // the guess.
    //
    // The guess costs space, because on the Moto the web view already stops
    // above the system navigation bar. Android hands the app a window that
    // ends at the bar rather than under it, and the 45px was then reserved a
    // second time on top of that: the nav capsule floated 45px up, and the
    // scrolling area lost the same 45px again. On a 411x821 viewport that is
    // a visible band of nothing across the bottom of every screen.
    //
    // env() is the actual measurement rather than a guess. A web view that is
    // already inset reports zero, which is correct and closes the gap. One
    // that is edge to edge reports the real bar, which is also correct, so
    // this holds on gesture navigation and on phones that lay out differently
    // instead of being tuned to this one.
    //
    // MainActivity reads the same insets from the window and sets these four
    // directly, which is the better source: from Android 15 the platform puts
    // the app edge to edge whether it asks to be or not, and the web view's
    // env() answers for display cutouts rather than for the system bars, so it
    // can report zero with the navigation bar sitting over the content. The
    // native numbers win either way round - this skips if they already landed,
    // and MainActivity writes over these if they have not.
    if (!window.__ccNativeSafeArea) {
      root.style.setProperty('--safe-area-top', 'env(safe-area-inset-top, 0px)');
      root.style.setProperty('--safe-area-bottom', 'env(safe-area-inset-bottom, 0px)');
      root.style.setProperty('--safe-area-left', 'env(safe-area-inset-left, 0px)');
      root.style.setProperty('--safe-area-right', 'env(safe-area-inset-right, 0px)');
    }
  }
  // Web: defaults (0px) from CSS are fine
};

// Run setup
setupSafeAreas();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <App />
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// A tapped button keeps focus on iOS, and every focus style it has then reads
// as a selection. Handled once, for every button in the app.
dropFocusAfterTap();
